import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, ConfigProvider, Select } from 'antd';
import { API_BASE_URL } from '../../config';
import { paginateOrders } from '../../utils/adminOrderTable.mjs';
import '../AdminDashboard.css';

const MOVEMENT_LABELS = {
    string_order: { label: 'String order', background: '#fde2e4', color: '#8a1c2b' },
    string_order_cancel: { label: 'Order cancelled', background: '#fff3cd', color: '#856404' },
    adjustment: { label: 'Adjustment', background: '#d1ecf1', color: '#0c5460' },
    purchase: { label: 'Purchase', background: '#d4edda', color: '#155724' },
    purchase_void: { label: 'Purchase void', background: '#e2e3e5', color: '#383d41' },
};

const formatQty = (v) => {
    const n = Number(v);
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const EMPTY_ADD_ITEM_FORM = {
    name: '',
    sku: '',
    rate: '',
    stock_on_hand: '',
    item_type: 'inventory',
    status: 'active',
};

function ItemsTab() {
    const navigate = useNavigate();
    const token = localStorage.getItem('adminToken');

    const authHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    }), [token]);

    const handleAuthError = useCallback((res) => {
        if (res.status === 401) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUsername');
            navigate('/admin');
            return true;
        }
        return false;
    }, [navigate]);

    const [zohoItems, setZohoItems] = useState([]);
    const [itemsLoading, setItemsLoading] = useState(false);
    const [itemsSearch, setItemsSearch] = useState('');
    const [itemsPage, setItemsPage] = useState(1);

    const [showAddItemModal, setShowAddItemModal] = useState(false);
    const [addItemForm, setAddItemForm] = useState(EMPTY_ADD_ITEM_FORM);
    const [addItemSaving, setAddItemSaving] = useState(false);
    const [addItemError, setAddItemError] = useState('');

    const [logItem, setLogItem] = useState(null);
    const [logRows, setLogRows] = useState([]);
    const [logLoading, setLogLoading] = useState(false);
    const [logError, setLogError] = useState('');

    const [editItemTarget, setEditItemTarget] = useState(null);
    const [editItemForm, setEditItemForm] = useState(EMPTY_ADD_ITEM_FORM);
    const [editItemSaving, setEditItemSaving] = useState(false);
    const [editItemError, setEditItemError] = useState('');

    const fetchItems = useCallback(async () => {
        setItemsLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/items`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            setZohoItems(data.items || []);
        } catch (err) {
            // Non-fatal — table just stays empty and the user can retry.
        } finally {
            setItemsLoading(false);
        }
    }, [authHeaders, handleAuthError]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const openLog = async (item) => {
        setLogItem(item);
        setLogRows([]);
        setLogError('');
        setLogLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/inventory/movements?item_id=${encodeURIComponent(item.item_id)}&limit=2000`, {
                headers: authHeaders(),
            });
            if (handleAuthError(res)) return;
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to load the stock log');
            setLogRows(data.movements || []);
        } catch (err) {
            setLogError(err.message);
        } finally {
            setLogLoading(false);
        }
    };

    const closeAddItemModal = () => {
        setShowAddItemModal(false);
        setAddItemForm(EMPTY_ADD_ITEM_FORM);
        setAddItemError('');
    };

    const handleAddItem = async () => {
        if (!addItemForm.name.trim()) {
            setAddItemError('Item name is required');
            return;
        }
        setAddItemSaving(true);
        setAddItemError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/items`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(addItemForm),
            });
            if (handleAuthError(res)) return;
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to add item');
            closeAddItemModal();
            fetchItems();
        } catch (err) {
            setAddItemError(err.message);
        } finally {
            setAddItemSaving(false);
        }
    };

    const openEditItemModal = (item) => {
        setEditItemTarget(item);
        setEditItemForm({
            name: item.name || '',
            sku: item.sku || '',
            rate: item.rate != null ? String(item.rate) : '',
            stock_on_hand: item.stock_on_hand != null ? Number(item.stock_on_hand).toFixed(1) : '0.0',
            item_type: item.item_type || 'inventory',
            status: item.status || 'active',
        });
        setEditItemError('');
    };

    const closeEditItemModal = () => {
        setEditItemTarget(null);
        setEditItemForm(EMPTY_ADD_ITEM_FORM);
        setEditItemError('');
    };

    const handleUpdateItem = async () => {
        if (!editItemForm.name.trim()) {
            setEditItemError('Item name is required');
            return;
        }
        if (editItemForm.stock_on_hand === '' || isNaN(parseFloat(editItemForm.stock_on_hand))) {
            setEditItemError('Enter a valid stock quantity');
            return;
        }
        setEditItemSaving(true);
        setEditItemError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/items/${encodeURIComponent(editItemTarget.item_id)}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({
                    name: editItemForm.name,
                    sku: editItemForm.sku,
                    item_type: editItemForm.item_type,
                    stock_on_hand: editItemForm.stock_on_hand,
                }),
            });
            if (handleAuthError(res)) return;
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to update item');
            closeEditItemModal();
            fetchItems();
        } catch (err) {
            setEditItemError(err.message);
        } finally {
            setEditItemSaving(false);
        }
    };

    const q = itemsSearch.trim().toLowerCase();
    const filtered = q
        ? zohoItems.filter(it =>
            it.name?.toLowerCase().includes(q) ||
            it.sku?.toLowerCase().includes(q))
        : zohoItems;
    const paginated = paginateOrders(filtered, itemsPage, 25);

    return (
        <div className="tab-content">
            <div className="section-header">
                <h2>Items</h2>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn-add" onClick={() => setShowAddItemModal(true)}>
                        + Add Item
                    </button>
                    {/* Zoho item sync is disabled: it overwrites stock_on_hand and wipes string order deductions */}
                    <button
                        className="btn-add"
                        disabled
                        title="Disabled: syncing from Zoho would overwrite stock quantities"
                        style={{ background: '#ccc', cursor: 'not-allowed' }}
                    >
                        Sync Items
                    </button>
                </div>
            </div>

            <div style={{ margin: '12px 0' }}>
                <Input
                    size="large"
                    placeholder="Search by name or SKU..."
                    value={itemsSearch}
                    onChange={e => { setItemsSearch(e.target.value); setItemsPage(1); }}
                    allowClear
                    style={{ maxWidth: 360 }}
                />
            </div>

            {itemsLoading ? (
                <div className="admin-loading">Loading...</div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>SKU</th>
                                <th>Rate</th>
                                <th>Stock Qty</th>
                                <th>Type</th>
                                <th>Tax</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.items.map(it => (
                                <tr key={it.item_id}>
                                    <td style={{ fontWeight: 600 }}>{it.name || '—'}</td>
                                    <td><code style={{ fontSize: '0.78rem' }}>{it.sku || '—'}</code></td>
                                    <td>{it.rate != null ? `SGD ${Number(it.rate).toFixed(2)}` : '—'}</td>
                                    <td>{it.stock_on_hand != null ? Number(it.stock_on_hand).toFixed(1) : '—'}</td>
                                    <td style={{ textTransform: 'capitalize' }}>{it.item_type || '—'}</td>
                                    <td>{it.tax_name ? `${it.tax_name} (${Number(it.tax_percentage).toFixed(1)}%)` : '—'}</td>
                                    <td>
                                        <span className="order-status-pill" style={{
                                            background: it.status === 'active' ? '#d4edda' : '#e2e3e5',
                                            color: it.status === 'active' ? '#155724' : '#383d41',
                                        }}>
                                            {it.status || '—'}
                                        </span>
                                    </td>
                                    <td className="action-cell">
                                        <button className="btn-cancel" onClick={() => openEditItemModal(it)}>Edit</button>
                                        <button className="btn-cancel" style={{ marginLeft: 6 }} onClick={() => openLog(it)}>Log</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filtered.length === 0 && (
                        <div className="no-data">{q ? 'No items match your search.' : 'No items yet. Click "Add Item" to get started.'}</div>
                    )}
                    {filtered.length > 0 && (
                        <div className="table-pagination">
                            <span className="table-pagination-summary">
                                Showing {paginated.items.length} of {paginated.totalItems} item{paginated.totalItems !== 1 ? 's' : ''}{q ? ` (filtered from ${zohoItems.length})` : ''}
                            </span>
                            <div className="table-pagination-controls">
                                <button type="button" className="btn-cancel table-page-btn" onClick={() => setItemsPage(p => Math.max(1, p - 1))} disabled={paginated.currentPage === 1}>Previous</button>
                                <span className="table-page-indicator">Page {paginated.currentPage} of {paginated.totalPages}</span>
                                <button type="button" className="btn-save table-page-btn" onClick={() => setItemsPage(p => Math.min(paginated.totalPages, p + 1))} disabled={paginated.currentPage === paginated.totalPages}>Next</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {logItem && (
                <div className="modal-overlay" onClick={() => setLogItem(null)}>
                    <div className="modal-content" style={{ maxWidth: 900 }} onClick={e => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setLogItem(null)}>×</button>
                        <h3 style={{ marginBottom: 4 }}>Stock Log: {logItem.name}</h3>
                        <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: 16 }}>
                            Current stock: <strong style={{ color: '#333' }}>{logItem.stock_on_hand != null ? formatQty(logItem.stock_on_hand) : '—'}</strong>
                            {!logLoading && !logError && ` · ${logRows.length} change${logRows.length !== 1 ? 's' : ''}, newest first`}
                        </div>
                        {logLoading ? (
                            <div className="admin-loading">Loading...</div>
                        ) : logError ? (
                            <div className="no-data" style={{ color: '#c41e3a' }}>{logError}</div>
                        ) : logRows.length === 0 ? (
                            <div className="no-data">No stock changes recorded for this item yet.</div>
                        ) : (
                            <div className="admin-table-wrapper" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Type</th>
                                            <th style={{ textAlign: 'right' }}>Change</th>
                                            <th style={{ textAlign: 'right' }}>Before → After</th>
                                            <th>Details</th>
                                            <th>By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logRows.map(m => {
                                            const kind = MOVEMENT_LABELS[m.movement_type] || { label: m.movement_type, background: '#eee', color: '#333' };
                                            const change = Number(m.quantity_change);
                                            const details = [m.reason, m.note, m.purchase_no].filter(Boolean).join(' · ');
                                            return (
                                                <tr key={m.id}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{m.created_at ? new Date(m.created_at).toLocaleString('en-SG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                                                    <td>
                                                        <span className="order-status-pill" style={{ background: kind.background, color: kind.color }}>{kind.label}</span>
                                                    </td>
                                                    <td style={{ textAlign: 'right', fontWeight: 600, color: change < 0 ? '#c41e3a' : '#2e7d32' }}>
                                                        {change > 0 ? '+' : ''}{formatQty(change)}
                                                    </td>
                                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                        {m.stock_before != null ? formatQty(m.stock_before) : '—'} → {m.stock_after != null ? formatQty(m.stock_after) : '—'}
                                                    </td>
                                                    <td>{details || '—'}</td>
                                                    <td>{m.created_by || '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showAddItemModal && (
                <div className="modal-overlay" onClick={closeAddItemModal}>
                    <div className="modal-content" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeAddItemModal}>×</button>
                        <h3 style={{ marginBottom: 16 }}>Add Item</h3>
                        <ConfigProvider theme={{ token: { colorPrimary: '#c41e3a', borderRadius: 8 } }}>
                            <Form layout="vertical" requiredMark="">
                                <Form.Item label="Item Name" required>
                                    <Input
                                        size="large"
                                        value={addItemForm.name}
                                        onChange={e => { setAddItemForm(p => ({ ...p, name: e.target.value })); setAddItemError(''); }}
                                        placeholder="e.g. Yonex BG65"
                                    />
                                </Form.Item>
                                <Form.Item label="SKU">
                                    <Input
                                        size="large"
                                        value={addItemForm.sku}
                                        onChange={e => setAddItemForm(p => ({ ...p, sku: e.target.value }))}
                                    />
                                </Form.Item>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <Form.Item label="Rate (SGD)" style={{ flex: 1 }}>
                                        <Input
                                            size="large"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={addItemForm.rate}
                                            onChange={e => setAddItemForm(p => ({ ...p, rate: e.target.value }))}
                                        />
                                    </Form.Item>
                                    <Form.Item label="Stock Qty" style={{ flex: 1 }}>
                                        <Input
                                            size="large"
                                            type="number"
                                            min="0"
                                            step="0.1"
                                            value={addItemForm.stock_on_hand}
                                            onChange={e => setAddItemForm(p => ({ ...p, stock_on_hand: e.target.value }))}
                                        />
                                    </Form.Item>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <Form.Item label="Type" style={{ flex: 1 }}>
                                        <Select
                                            size="large"
                                            value={addItemForm.item_type}
                                            onChange={val => setAddItemForm(p => ({ ...p, item_type: val }))}
                                            options={[
                                                { value: 'inventory', label: 'Inventory' },
                                                { value: 'service', label: 'Service' },
                                                { value: 'sales', label: 'Sales' },
                                            ]}
                                        />
                                    </Form.Item>
                                    <Form.Item label="Status" style={{ flex: 1 }}>
                                        <Select
                                            size="large"
                                            value={addItemForm.status}
                                            onChange={val => setAddItemForm(p => ({ ...p, status: val }))}
                                            options={[
                                                { value: 'active', label: 'Active' },
                                                { value: 'inactive', label: 'Inactive' },
                                            ]}
                                        />
                                    </Form.Item>
                                </div>
                                {addItemError && (
                                    <div style={{
                                        background: '#fff2f0',
                                        border: '1px solid #ffccc7',
                                        borderRadius: 8,
                                        padding: '8px 12px',
                                        marginBottom: 12,
                                        fontSize: '0.85rem',
                                        color: '#cf1322',
                                    }}>
                                        {addItemError}
                                    </div>
                                )}
                                <div className="form-actions">
                                    <button type="button" className="btn-cancel" onClick={closeAddItemModal}>Cancel</button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        loading={addItemSaving}
                                        disabled={!addItemForm.name.trim()}
                                        onClick={handleAddItem}
                                    >
                                        Save Item
                                    </Button>
                                </div>
                            </Form>
                        </ConfigProvider>
                    </div>
                </div>
            )}

            {editItemTarget && (
                <div className="modal-overlay" onClick={closeEditItemModal}>
                    <div className="modal-content" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeEditItemModal}>×</button>
                        <h3 style={{ marginBottom: 16 }}>Edit Item</h3>
                        <ConfigProvider theme={{ token: { colorPrimary: '#c41e3a', borderRadius: 8 } }}>
                            <Form layout="vertical" requiredMark="">
                                <Form.Item label="Item Name" required>
                                    <Input
                                        size="large"
                                        autoFocus
                                        value={editItemForm.name}
                                        onChange={e => { setEditItemForm(p => ({ ...p, name: e.target.value })); setEditItemError(''); }}
                                    />
                                </Form.Item>
                                <Form.Item label="SKU">
                                    <Input
                                        size="large"
                                        value={editItemForm.sku}
                                        onChange={e => setEditItemForm(p => ({ ...p, sku: e.target.value }))}
                                    />
                                </Form.Item>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <Form.Item label="Type" style={{ flex: 1 }}>
                                        <Select
                                            size="large"
                                            value={editItemForm.item_type}
                                            onChange={val => setEditItemForm(p => ({ ...p, item_type: val }))}
                                            options={[
                                                { value: 'inventory', label: 'Inventory' },
                                                { value: 'service', label: 'Service' },
                                                { value: 'sales', label: 'Sales' },
                                            ]}
                                        />
                                    </Form.Item>
                                    <Form.Item label="Stock Qty" style={{ flex: 1 }} required>
                                        <Input
                                            size="large"
                                            type="number"
                                            min="0"
                                            step="0.1"
                                            value={editItemForm.stock_on_hand}
                                            onChange={e => { setEditItemForm(p => ({ ...p, stock_on_hand: e.target.value })); setEditItemError(''); }}
                                            onKeyDown={e => e.key === 'Enter' && handleUpdateItem()}
                                        />
                                    </Form.Item>
                                </div>
                                {editItemError && (
                                    <div style={{
                                        background: '#fff2f0',
                                        border: '1px solid #ffccc7',
                                        borderRadius: 8,
                                        padding: '8px 12px',
                                        marginBottom: 12,
                                        fontSize: '0.85rem',
                                        color: '#cf1322',
                                    }}>
                                        {editItemError}
                                    </div>
                                )}
                                <div className="form-actions">
                                    <button type="button" className="btn-cancel" onClick={closeEditItemModal}>Cancel</button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        loading={editItemSaving}
                                        disabled={!editItemForm.name.trim()}
                                        onClick={handleUpdateItem}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </Form>
                        </ConfigProvider>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ItemsTab;
